import { CreateDraftDocument } from '../graphql/__generated__/CreateDraftMutation.interface';
import { ComposeOperation as SchemaOperation, MentionInfoInput } from 'owa-graph-schema';
import { getApolloClient } from 'owa-apollo';
import { getISOString } from 'owa-datetime';
import { ComposeViewState, ComposeOperation as ViewStateOperation } from 'owa-mail-compose-store';
import isPrimarySmtp from 'owa-mail-proxy-address/lib/utils/isPrimarySmtp';
import Message from 'owa-service/lib/contract/Message';
import SmartResponseType from 'owa-service/lib/contract/SmartResponseType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    computeLabelPropertyValue,
    computeLabelModifyHeader,
} from 'owa-mail-protection/lib/utils/clp/stampCLPLabelInExtendedProperty';
import ItemId from 'owa-service/lib/contract/ItemId';

export default async function createItemViaGraphQL(
    messageType: Message | SmartResponseType,
    timeFormat: string,
    viewState: ComposeViewState
) {
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            draft: {
                ToRecipients: messageType.ToRecipients,
                CcRecipients: messageType.CcRecipients,
                BccRecipients: messageType.BccRecipients,
                Subject: messageType.Subject,
                Body: {
                    Value: messageType.Body.Value,
                    DataUriCount: messageType.Body.DataUriCount,
                    BodyType: messageType.Body.BodyType,
                },
                Importance: messageType.Importance,
                IsReadReceiptRequested: messageType.IsReadReceiptRequested,
                IsDeliveryReceiptRequested: messageType.IsDeliveryReceiptRequested,
                UpdateResponseItemId: (messageType as Partial<SmartResponseType>)
                    .UpdateResponseItemId,
                ReferenceItemId: (messageType as Partial<SmartResponseType>)
                    .ReferenceItemId as ItemId,
                ReferenceItemDocumentId: (messageType as Partial<SmartResponseType>)
                    .ReferenceItemDocumentId,
                atMentionedRecipients: createListOfMentionedSMTPs(viewState),
                recipientsAddedViaAtMention: viewState.mentionsPicker.mentionsAddedRecipients,
                connectedAccountSharingGuid: getConnectedAccountSharingGuid(viewState),
                deferredSendTime: !!viewState.deferredSendTime
                    ? getISOString(viewState.deferredSendTime)
                    : undefined,
                clpLabelModifificationHeader: computeLabelModifyHeader(
                    viewState.protectionViewState.clpViewState
                ),
                clpLabelProperty: computeLabelPropertyValue(
                    viewState.protectionViewState.clpViewState
                ),
                messageClassification: getMessageClassification(viewState),
                originalSmtpFromAddress: getOriginalSmtpFromAddress(viewState),
                operation: getOperationMapping(viewState.operation),
            },
            timeFormat,
        },
        mutation: CreateDraftDocument,
    });
    const draftData = {
        Items: [
            {
                ...result.data.createDraft.draft,
            },
        ],
        ResponseClass: 'Success',
    };
    return {
        ResponseMessages: {
            Items: [draftData],
        },
    };
}

function getOperationMapping(operation: ViewStateOperation): SchemaOperation {
    switch (operation) {
        case ViewStateOperation.New:
            return 'New';
        case ViewStateOperation.Reply:
            return 'Reply';
        case ViewStateOperation.ReplyAll:
            return 'ReplyAll';
        case ViewStateOperation.Forward:
            return 'Forward';
        case ViewStateOperation.EditDraft:
        case ViewStateOperation.Approve:
        case ViewStateOperation.Reject:
            throw new Error('Unexpected compose operation. ');
    }
}

export function isSupportedOperation(operation: ViewStateOperation): boolean {
    switch (operation) {
        case ViewStateOperation.New:
        case ViewStateOperation.Reply:
        case ViewStateOperation.ReplyAll:
        case ViewStateOperation.Forward:
            return true;
        case ViewStateOperation.EditDraft:
        case ViewStateOperation.Approve:
        case ViewStateOperation.Reject:
            return false;
    }
}

function createListOfMentionedSMTPs(viewState: ComposeViewState): MentionInfoInput[] {
    const { atMentionTuples } = viewState.mentionsPicker;
    const mentionInfos: MentionInfoInput[] = [];
    Object.keys(atMentionTuples).forEach(key => {
        const persona = atMentionTuples[key];
        mentionInfos.push({
            key,
            emailAddress: persona.EmailAddress,
        });
    });
    return mentionInfos;
}

function getConnectedAccountSharingGuid(viewState: ComposeViewState): string | undefined {
    const from = viewState.fromViewState.from;
    const connectAccountInfos = getUserConfiguration().SessionSettings.ConnectedAccountInfos;
    if (connectAccountInfos) {
        for (let i = 0; i < connectAccountInfos.length; i++) {
            const connectAccountInfo = connectAccountInfos[i];
            if (connectAccountInfo.EmailAddress == from.email.EmailAddress) {
                return connectAccountInfo.SubscriptionGuid;
            }
        }
    }

    return undefined;
}

function getMessageClassification(viewState: ComposeViewState) {
    const classificationViewState = viewState.protectionViewState.classificationViewState;
    return classificationViewState.messageClassification
        ? {
              ...classificationViewState.messageClassification,
              isClassified: true,
          }
        : undefined;
}

function getOriginalSmtpFromAddress(viewState: ComposeViewState) {
    const fromAddress = viewState.fromViewState?.from?.email?.EmailAddress;
    if (fromAddress && !isPrimarySmtp(fromAddress)) {
        return fromAddress;
    }
    return undefined;
}
